
    import React, { useState, useEffect } from 'react';
import { Card, Button, Spinner, Toast, Row, Col, Badge, Alert } from 'react-bootstrap';
import { Whatsapp } from 'react-bootstrap-icons';
// Generated by Copilot — Migrated from Userfront to Clerk
import useAuthHeaders from '../hooks/useAuthHeaders';

// Generated by Copilot
const planes = [
  {
    id: 1,
    nombre: 'Básico',
    lookupKey: 'Basic',
    precio: 499,
    descripcion: 'Ideal para emprendedores',
    caracteristicas: [
      'Hasta 100 RFCs',
      'CSF, Opinión de cumplimiento, declaraciones ilimitadas',
      'Notificaciones y comunicados del SAT',
      'Opinión del IMSS',
      'Consulta EFOS (anexo 64-B)',
      'Soporte por WhatsApp'
    ],
    destacado: false
  },
  {
    id: 2,
    nombre: 'Pro',
    lookupKey: 'Pro',
    precio: 999,
    descripcion: 'Para pequeñas y medianas empresas',
    caracteristicas: [
      'Todo lo de Básico:',
      'Hasta 500 RFCs',
      'Descarga de Facturas (Hasta 50,000 CFDI al mes)',
      'Descarga de Facturas por Web Service del SAT asíncrona (200 mil al mes)',
      'Descarga de Retenciones (Hasta 50,000 al mes)',
      'Validación de XMLs',
      'Repse (beta)'
    ],
    destacado: true
  },
  {
    id: 3,
    nombre: 'Enterprise',
    lookupKey: 'Enterprise',
    precio: null, // Precio personalizado
    descripcion: 'Solución corporativa completa',
    caracteristicas: [
      'Descargas ilimitadas',
      'Soporte dedicado',
      'Gestor de cuenta asignado',
      'Tenant dedicado (opcional)',
      'Capacitación y onboarding'
    ],
    destacado: false,
    esContacto: true // Plan que requiere contacto directo
  }
];

const TRIAL_DAYS = parseInt(process.env.REACT_APP_TRIAL_DAYS, 10) || 15;

const Suscripciones = () => {
  const [loading, setLoading] = useState(null);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [isFreeTrial, setIsFreeTrial] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const { userId, getToken } = useAuthHeaders();

  // Verificar suscripción actual al cargar
  useEffect(() => {
    const checkCurrentSubscription = async () => {
      if (!userId) {
        setLoadingSubscription(false);
        return;
      }

      try {
        const token = await getToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/Stripe/current-subscription`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.hasSubscription) {
            setCurrentSubscription(data);
          }
          setIsFreeTrial(data.isFreeTrial ?? false);
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
      } finally {
        setLoadingSubscription(false);
      }
    };

    checkCurrentSubscription();
  }, [userId, getToken]);

  // Generated by Copilot - Manejar redirección al portal de facturación
  const handleManageBilling = async () => {
    if (!userId) {
      setToastMessage('Debes iniciar sesión para continuar');
      setToastVariant('danger');
      setShowToast(true);
      return;
    }

    setLoadingPortal(true);
    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Stripe/update-billing-portal-session`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al abrir el portal de facturación');
      }

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error:', error);
      setToastMessage(error.message);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setLoadingPortal(false);
    }
  };

  const handleCheckout = async (lookupKey) => {
    if (!userId) {
      setToastMessage('Debes iniciar sesión para continuar');
      setToastVariant('danger');
      setShowToast(true);
      return;
    }

    if (currentSubscription) {
      setToastMessage('Ya tienes una suscripción activa');
      setToastVariant('warning');
      setShowToast(true);
      return;
    }

    setLoading(lookupKey);
    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Stripe/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            lookupKey: lookupKey,
            userId: userId,
            promotionCode: couponCode.trim() || null
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear la sesión de checkout');
      }

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        setToastMessage('Sesión de checkout creada exitosamente');
        setToastVariant('success');
        setShowToast(true);
      }

    } catch (error) {
      console.error('Error:', error);
      setToastMessage(error.message);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setLoading(null);
    }
  };

  const formatPrice = (precio) => {
    if (precio === null) return 'Personalizado';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(precio);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isCurrentPlan = (lookupKey) => {
    return currentSubscription?.lookupKey === lookupKey;
  };

  const handleWhatsAppContact = () => {
    const mensaje = encodeURIComponent('Hola, me interesa el plan Enterprise para mi empresa. ¿Podrían darme más información?');
    window.open(`https://wa.me/529981957130?text=${mensaje}`, '_blank');
  };

  if (loadingSubscription) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Cargando información de suscripción...</p>
      </div>
    );
  }

  return (
    <section className="py-4">
      <div className="text-center mb-5">
        <h2 className="fw-bold">Elige tu Plan</h2>
        <p className="text-muted">Selecciona el plan que mejor se adapte a tus necesidades</p>
      </div>

      {currentSubscription && (
        <Alert variant="info" className="mb-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
            <div className="text-center text-md-start mb-3 mb-md-0">
              <Alert.Heading className="mb-2">
                <i className="bi bi-check-circle-fill me-2"></i>
                Ya tienes una suscripción activa
              </Alert.Heading>
              <p className="mb-0">
                Tu plan actual es <strong>{planes.find(p => p.lookupKey === currentSubscription.lookupKey)?.nombre || 'Plan Activo'}</strong>.
                {currentSubscription.currentPeriodEnd && (
                  <> Válido hasta el <strong>{formatDate(currentSubscription.currentPeriodEnd)}</strong>.</>
                )}
              </p>
            </div>
            <Button
              variant="outline-primary"
              onClick={handleManageBilling}
              disabled={loadingPortal}
              className="mt-2 mt-md-0"
            >
              {loadingPortal ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Cargando...
                </>
              ) : (
                <>
                  <i className="bi bi-gear me-2"></i>
                  Administrar Suscripción
                </>
              )}
            </Button>
          </div>
        </Alert>
      )}

      {isFreeTrial && (
        <Alert variant="warning" className="mb-4">
          <Alert.Heading>Tu plan gratuito tiene acceso limitado</Alert.Heading>
          <p className="mb-2">Con tu cuenta gratuita tienes acceso a:</p>
          <ul className="mb-2">
            <li><strong>3</strong> Constancias de Situación Fiscal</li>
            <li><strong>3</strong> Opiniones de Cumplimiento del SAT</li>
            <li><strong>3</strong> Opiniones del IMSS</li>
            <li><strong>3</strong> Consultas de Información Fiscal</li>
            <li><strong>500</strong> Facturas</li>
          </ul>
          <p className="mb-0">
            Suscríbete a cualquier plan y obtén <strong>{TRIAL_DAYS} días completos sin costo</strong> — sin límites, sin restricciones.
          </p>
        </Alert>
      )}

      {!currentSubscription && (
        <div className="d-flex justify-content-center mb-4">
          <div style={{ maxWidth: 360, width: '100%' }}>
            <label className="form-label text-muted small mb-1">¿Tienes un código de descuento?</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Ej. PROMO20"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                maxLength={50}
              />
              {couponCode && (
                <button className="btn btn-outline-secondary" onClick={() => setCouponCode('')}>
                  ✕
                </button>
              )}
            </div>
            {couponCode.trim() && (
              <small className="text-success">Con este código pagarás desde hoy con descuento (sin período de prueba).</small>
            )}
          </div>
        </div>
      )}

      <Row className="g-4 justify-content-center">
        {planes.map((plan) => {
          const isPlanActive = isCurrentPlan(plan.lookupKey);
          
          return (
            <Col key={plan.id} xs={12} md={6} lg={4}>
              <Card 
                className={`h-100 shadow-sm ${isPlanActive ? 'border-success border-3' : plan.destacado && !currentSubscription ? 'border-primary border-2' : ''}`}
                style={{ 
                  transition: 'transform 0.2s',
                  opacity: currentSubscription && !isPlanActive ? 0.6 : 1
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {isPlanActive && (
                  <div className="text-center pt-2">
                    <Badge bg="success" className="px-3 py-2">
                      <i className="bi bi-check-circle me-1"></i>
                      Tu Plan Actual
                    </Badge>
                  </div>
                )}
                {!isPlanActive && plan.destacado && !currentSubscription && (
                  <div className="text-center pt-2">
                    <Badge bg="primary" className="px-3 py-2">Más Popular</Badge>
                  </div>
                )}
                {isFreeTrial && !isPlanActive && !plan.esContacto && !couponCode.trim() && (
                  <div className="text-center pt-2">
                    <Badge bg="success" className="px-3 py-2">{TRIAL_DAYS} días gratis</Badge>
                  </div>
                )}
                {couponCode.trim() && !isPlanActive && !plan.esContacto && (
                  <div className="text-center pt-2">
                    <Badge bg="warning" text="dark" className="px-3 py-2">Cupón aplicado</Badge>
                  </div>
                )}
                <Card.Body className="d-flex flex-column p-4">
                  <div className="text-center mb-4">
                    <h4 className="fw-bold mb-1">{plan.nombre}</h4>
                    <p className="text-muted small mb-3">{plan.descripcion}</p>
                    <div className="mb-2">
                      <span className={`${plan.precio === null ? 'h4' : 'display-5'} fw-bold`}>{formatPrice(plan.precio)}</span>
                      {plan.precio !== null && <span className="text-muted"> / mes</span>}
                    </div>
                    {plan.precio !== null && <small className="text-muted">+ IVA</small>}
                  </div>

                  <ul className="list-unstyled flex-grow-1 mb-4">
                    {plan.caracteristicas.map((caracteristica, index) => (
                      <li key={index} className="mb-2 d-flex align-items-center">
                        <svg 
                          className="me-2 text-success" 
                          width="16" 
                          height="16" 
                          fill="currentColor" 
                          viewBox="0 0 16 16"
                        >
                          <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                        </svg>
                        <span>{caracteristica}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="d-grid mt-auto gap-2">
                    {isPlanActive ? (
                      <Button
                        variant="success"
                        size="lg"
                        disabled
                      >
                        <i className="bi bi-check-lg me-2"></i>
                        Plan Activo
                      </Button>
                    ) : plan.esContacto ? (
                      <Button
                        variant="outline-success"
                        size="lg"
                        onClick={handleWhatsAppContact}
                        className="d-flex align-items-center justify-content-center"
                      >
                        <Whatsapp className="me-2" size={20} />
                        Contáctanos
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant={currentSubscription ? 'secondary' : (plan.destacado ? 'primary' : 'outline-primary')}
                          size="lg"
                          onClick={() => handleCheckout(plan.lookupKey)}
                          disabled={loading !== null || currentSubscription !== null}
                        >
                          {loading === plan.lookupKey ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                              />
                              Procesando...
                            </>
                          ) : currentSubscription ? (
                            'No disponible'
                          ) : couponCode.trim() ? (
                            'Suscribirse'
                          ) : isFreeTrial ? (
                            `Iniciar ${TRIAL_DAYS} días gratis`
                          ) : (
                            'Suscribirse'
                          )}
                        </Button>
                        {!currentSubscription && (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => {
                              const mensaje = encodeURIComponent(`Hola, tengo una duda sobre el plan ${plan.nombre}`);
                              window.open(`https://wa.me/529981957130?text=${mensaje}`, '_blank');
                            }}
                            className="d-flex align-items-center justify-content-center"
                          >
                            <Whatsapp className="me-2" size={16} />
                            ¿Dudas? Contáctanos
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        delay={5000}
        autohide
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          minWidth: '300px',
          zIndex: 9999
        }}
        bg={toastVariant}
      >
        <Toast.Header>
          <strong className="me-auto">{toastVariant === 'success' ? 'Éxito' : toastVariant === 'warning' ? 'Aviso' : 'Error'}</strong>
        </Toast.Header>
        <Toast.Body className="text-white">
          {toastMessage}
        </Toast.Body>
      </Toast>
    </section>
  );
};

export default Suscripciones;